import { Box, Link, Stack, Tooltip, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
    CustomizeDateModal,
    DateFilterOptions,
    DateFilterOptionsLabels,
    getPresetDate,
} from '../../../components/DateFilterPicker'
import { GenericTable } from '../../../components/GenericTable'
import { AccountInfo, useAccount } from '../../../context/account_context'
import { BranchInfo, useBranch } from '../../../context/branch_context'
import { CashierInfo } from '../../../context/cashier_context'
import { useCoin } from '../../../context/coin_context'
import { SystemBranchEnum, useConfigs } from '../../../context/configs_context'
import { useLoading } from '../../../context/loading_context'
import { useSnack } from '../../../context/snack_context'
import { listAccounts } from '../../../services/account'
import { listCashiers } from '../../../services/cashier'
import { listLogs } from '../../../services/log'
import {
    getDateInDDMMYYYYFormat,
    getDateInIsoFormat,
    getDateString,
    getTimeAgo,
} from '../../../utils/maks'
import GenericModal from '../../../components/GenericModal'
import JsonView from '@uiw/react-json-view'
import { vscodeTheme } from '@uiw/react-json-view/vscode';

export interface LogInfo {
    id: number
    date: string
    hour: string
    isSummerTime: boolean
    action: string
    module: string
    description: string | null
    requisition: string
    account: AccountInfo
    cashier: CashierInfo
    branch: BranchInfo | null
}

export interface LogsFilters {
    page: number
    rows: number
    rowsCount: number
    accounts: AccountInfo[]
    cashiers: CashierInfo[]
    branches: BranchInfo[]
    actions: string[]
    modules: string[]
    date: {
        iniD: Date
        endD: Date
    } | null
}

const modules = [
    'Venda',
    'Compra',
    'Produto',
    'Cliente',
    'Fornecedor',
    'Gasto',
    'Carga estoque',
    'Transferência estoque',
    'Caixa',
    'Pagamento compra',
    'Pagamento venda',
    'Login',
]

const actions = ['Criar', 'Editar', 'Cancelar']

export const LogHome = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const loading = useLoading()
    const snack = useSnack()
    const [logs, setLogs] = useState<LogInfo[]>([])
    const [search, setSearch] = useState('')
    const timeoutRef = useRef<NodeJS.Timeout>()
    const [filters, setFilters] = useState<LogsFilters>({
        page: 1,
        rows: 10,
        rowsCount: 0,
        accounts: [],
        cashiers: [],
        branches: [],
        actions: [],
        modules: [],
        date: null,
    })
    const { configs } = useConfigs()
    const { accountInfo, accountRules } = useAccount()
    const { branches } = useBranch()
    const { coins } = useCoin()
    const [accounts, setAccounts] = useState<AccountInfo[]>([])
    const [cashiers, setCashiers] = useState<CashierInfo[]>([])
    const [openDateModal, setOpenDateModal] = useState(false)
    const [selectedLog, setSelectedLog] = useState<LogInfo | null>(null)

    const searchCashiers = async (value: string) => {
        if (!value) return
        try {
            const res = await listCashiers({
                page: 1,
                rows: 10,
                search: value,
            })
            setCashiers(res.data)
        } catch (error) {}
    }

    const searchAccounts = async (value: string) => {
        if (!value || !accountRules.length) return
        try {
            const res = await listAccounts(
                {
                    page: 1,
                    rows: 10,
                    search: value,
                },
                accountRules,
                coins
            )
            setAccounts(res.data)
        } catch (error) {}
    }

    useEffect(() => {
        getLogs(filters, search)
    }, [])

    const getLogs = async (filters: LogsFilters, search: string, dontSetEmpty?: boolean) => {
        loading.logicShow()
        try {
            const res = await listLogs({
                skip: filters.page,
                take: filters.rows,
                search: search || undefined,
                id_caixa: filters.cashiers.map((c) => c.id ?? 0),
                id_usuario: filters.accounts.map((a) => a.id ?? 0),
                id_filial: filters.branches.map((b) => b.id ?? 0),
                data_inicial: filters.date
                    ? getDateInIsoFormat(filters.date.iniD)
                    : undefined,
                data_final: filters.date
                    ? getDateInIsoFormat(filters.date.endD)
                    : undefined,
                acao: filters.actions,
                modulo: filters.modules,
            })

            setLogs(res.data)
            if (!dontSetEmpty || res.data.length) {
                setFilters({
                    ...filters,
                    rowsCount: res.count,
                })
                setLogs(res.data)
            }
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.logicHide()
    }

    const branchColumn =
        !accountInfo?.branch && configs.systemBranch === SystemBranchEnum.Branch
            ? [
                  {
                      name: 'Sucursal',
                      property: ['branch'],
                      parser: (value: any) => value?.name ?? '---',
                      hideXs: true,
                  },
              ]
            : []

    return (
        <>
            {!!selectedLog && (
                <GenericModal
                    open
                    handleClose={() => {
                        setSelectedLog(null)
                    }}
                    textButton=""
                    title=""
                    handleSubmit={() => {}}
                >
                    <Box width={'600px'}>
                        <JsonView value={JSON.parse(selectedLog.requisition)} style={vscodeTheme}/>
                    </Box>
                </GenericModal>
            )}
            <CustomizeDateModal
                open={openDateModal}
                handleClose={setOpenDateModal}
                returnDates={(iniD, endD) => {
                    setFilters({
                        ...filters,
                        date: {
                            iniD: iniD,
                            endD: endD,
                        },
                        page: 1,
                    })
                    getLogs({
                        ...filters,
                        date: {
                            iniD: iniD,
                            endD: endD,
                        },
                        page: 1,
                    }, search)
                }}
            />
            <Box>
                <GenericTable
                    title="bc8f4d88-870e-4bc9-9af9-dbd4d9b8aa98"
                    filters={[
                        {
                            name: 'Acción',
                            labelField: 'name',
                            valueField: 'id',
                            values: actions.map((v) => ({ id: v, name: v })),
                            selectedValues: filters.actions.map((v) => ({
                                id: v,
                                name: v,
                            })),
                        },
                        {
                            name: 'Modulo',
                            labelField: 'name',
                            valueField: 'id',
                            values: modules.map((v) => ({ id: v, name: v })),
                            selectedValues: filters.modules.map((v) => ({
                                id: v,
                                name: v,
                            })),
                        },
                        {
                            name: 'Cuenta',
                            labelField: 'name',
                            valueField: 'id',
                            values: accounts,
                            selectedValues: filters.accounts,
                            autoComplete: {
                                cb: (v) => {
                                    clearTimeout(timeoutRef.current)
                                    timeoutRef.current = setTimeout(() => {
                                        searchAccounts(v)
                                    }, 500)
                                },
                            },
                        },
                        {
                            name: 'Caja',
                            labelField: 'name',
                            valueField: 'id',
                            values: cashiers,
                            selectedValues: filters.cashiers,
                            autoComplete: {
                                cb: (v) => {
                                    clearTimeout(timeoutRef.current)
                                    timeoutRef.current = setTimeout(() => {
                                        searchCashiers(v)
                                    }, 500)
                                },
                            },
                        },
                        {
                            name: 'Fecha',
                            labelField: 'name',
                            valueField: 'id',
                            values: Object.keys(DateFilterOptions).map((k) => ({
                                id: DateFilterOptions[k],
                                name: DateFilterOptionsLabels[k],
                            })),
                            selectedValues: filters.date
                                ? [
                                      {
                                          name: `${getDateInDDMMYYYYFormat(
                                              filters.date.iniD
                                          )} até ${getDateInDDMMYYYYFormat(
                                              filters.date.endD
                                          )}`,
                                          id: null,
                                      },
                                  ]
                                : [],
                        },
                        {
                            name: 'Sucursal',
                            labelField: 'name',
                            valueField: 'id',
                            values: branches,
                            selectedValues: filters.branches,
                            hideSelection:
                                !!accountInfo?.branch ||
                                configs.systemBranch !==
                                    SystemBranchEnum.Branch,
                        },
                    ]}
                    search={search}
                    handleChangeSearch={(v) => {
                        setSearch(v)
                        if (v === search) return

                        clearTimeout(timeoutRef.current)
                        timeoutRef.current = setTimeout(() => {
                            getLogs({ ...filters, page: 1 }, v)
                        }, 500)
                    }}
                    handleChangeSelectedFilter={(selectedFilter) => {
                        if (selectedFilter.name === 'Acción') {
                            const index = filters.actions.findIndex(
                                (g) => g === selectedFilter.value.id
                            )
                            const newValues = [...filters.actions]
                            if (selectedFilter.action === 'add')
                                newValues.push(selectedFilter.value.id)
                            else newValues.splice(index, 1)

                            setFilters({
                                ...filters,
                                actions: newValues,
                                page: 1,
                            })
                            getLogs({
                                ...filters,
                                actions: newValues,
                                page: 1,
                            }, search)
                        }
                        if (selectedFilter.name === 'Modulo') {
                            const index = filters.modules.findIndex(
                                (g) => g === selectedFilter.value.id
                            )
                            const newValues = [...filters.modules]
                            if (selectedFilter.action === 'add')
                                newValues.push(selectedFilter.value.id)
                            else newValues.splice(index, 1)

                            setFilters({
                                ...filters,
                                modules: newValues,
                                page: 1,
                            })
                            getLogs({
                                ...filters,
                                modules: newValues,
                                page: 1,
                            }, search)
                        }
                        if (selectedFilter.name === 'Cuenta') {
                            const index = filters.accounts.findIndex(
                                (g) => g.id === selectedFilter.value.id
                            )
                            const newValues = [...filters.accounts]
                            if (selectedFilter.action === 'add')
                                newValues.push(selectedFilter.value)
                            else newValues.splice(index, 1)

                            setFilters({
                                ...filters,
                                accounts: newValues,
                                page: 1,
                            })
                            getLogs({
                                ...filters,
                                accounts: newValues,
                                page: 1,
                            }, search)
                        }
                        if (selectedFilter.name === 'Cuenta') {
                            const index = filters.accounts.findIndex(
                                (g) => g.id === selectedFilter.value.id
                            )
                            const newValues = [...filters.accounts]
                            if (selectedFilter.action === 'add')
                                newValues.push(selectedFilter.value)
                            else newValues.splice(index, 1)

                            setFilters({
                                ...filters,
                                accounts: newValues,
                                page: 1,
                            })
                            getLogs({
                                ...filters,
                                accounts: newValues,
                                page: 1,
                            }, search)
                        }
                        if (selectedFilter.name === 'Caja') {
                            const index = filters.cashiers.findIndex(
                                (g) => g.id === selectedFilter.value.id
                            )
                            const newValues = [...filters.cashiers]
                            if (selectedFilter.action === 'add')
                                newValues.push(selectedFilter.value)
                            else newValues.splice(index, 1)

                            setFilters({
                                ...filters,
                                cashiers: newValues,
                                page: 1,
                            })
                            getLogs({
                                ...filters,
                                cashiers: newValues,
                                page: 1,
                            }, search)
                        }
                        if (selectedFilter.name === 'Fecha') {
                            if (
                                selectedFilter.value?.id ===
                                DateFilterOptions.Customize
                            ) {
                                setOpenDateModal(true)
                                return
                            }

                            const dates =
                                selectedFilter.value?.id &&
                                selectedFilter.action === 'add'
                                    ? getPresetDate(selectedFilter.value.id)
                                    : null

                            setFilters({
                                ...filters,
                                date: dates
                                    ? {
                                          iniD: dates?.firstDate,
                                          endD: dates?.lastDate,
                                      }
                                    : null,
                                page: 1,
                            })
                            getLogs({
                                ...filters,
                                date: dates
                                    ? {
                                          iniD: dates?.firstDate,
                                          endD: dates?.lastDate,
                                      }
                                    : null,
                                page: 1,
                            }, search)
                        }
                        if (selectedFilter.name === 'Sucursal') {
                            const index = filters.branches.findIndex(
                                (g) => g.id === selectedFilter.value.id
                            )
                            const newValues = [...filters.branches]
                            if (selectedFilter.action === 'add')
                                newValues.push(selectedFilter.value)
                            else newValues.splice(index, 1)

                            setFilters({
                                ...filters,
                                branches: newValues,
                                page: 1,
                            })
                            getLogs({
                                ...filters,
                                branches: newValues,
                                page: 1,
                            }, search)
                        }
                    }}
                    handleChangeSelectedValues={(values) => {}}
                    selectedValues={[]}
                    identifier="id"
                    values={logs}
                    pagination={{
                        page: filters.page,
                        rows: filters.rows,
                        rowsCount: filters.rowsCount,
                        handleChangePagination: (page, rows) => {
                            getLogs({ ...filters, page, rows }, search, true)
                        },
                    }}
                    columns={[
                        { name: 'Acción', property: ['action'] },
                        { name: 'Modulo', property: ['module'] },
                        { name: 'Descrição', property: ['description'], parser: (v) => v || '---' },
                        {
                            name: 'Fecha',
                            property: ['date'],
                            customRender: (log: LogInfo, value) => {
                                const text = getDateString({
                                    date: log.date ?? '',
                                    hour: log.hour ?? '',
                                    configs: configs,
                                    isSummerTime: log.isSummerTime ?? false,
                                })
                                const timeagoText = `${t('Hace')} ${getTimeAgo({
                                    date: `${log.date?.split('T')[0]} ${
                                        log.hour
                                    }`,
                                    configs: configs,
                                    isSummerTime: log.isSummerTime ?? false,
                                })}`

                                return (
                                    <Stack direction={'column'}>
                                        <Tooltip title={text}>
                                            <Typography fontSize={'0.875rem'}>
                                                {text}
                                            </Typography>
                                        </Tooltip>
                                        <Tooltip title={timeagoText}>
                                            <Typography
                                                fontSize={'0.75rem'}
                                                color={'#71797E'}
                                            >
                                                {timeagoText}
                                            </Typography>
                                        </Tooltip>
                                    </Stack>
                                )
                            },
                        },
                        {
                            name: 'Usuário/Caja',
                            property: ['account', 'name'],
                            customRender: (log: LogInfo, value) => {
                                const text = log.account?.name
                                const subText = log.cashier?.name

                                return (
                                    <Stack direction={'column'}>
                                        <Tooltip title={text}>
                                            <Typography fontSize={'0.875rem'}>
                                                {text}
                                            </Typography>
                                        </Tooltip>
                                        <Tooltip title={subText}>
                                            <Typography
                                                fontSize={'0.75rem'}
                                                color={'#71797E'}
                                            >
                                                {subText}
                                            </Typography>
                                        </Tooltip>
                                    </Stack>
                                )
                            },
                        },
                        {
                            name: 'Requisição',
                            property: ['requisition'],
                            customRender: (log: LogInfo, value) => {
                                return (
                                    <Link
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => setSelectedLog(log)}
                                    >
                                        Visualizar
                                    </Link>
                                )
                            },
                        },
                        ...branchColumn,
                    ]}
                />
            </Box>
        </>
    )
}
